import React, { useEffect, useMemo } from 'react';
import Page from '../page';
import Loader from '../loader/loader';
import { useGetSiteOpsQuery } from '../../store/slices/apiSlice/siteops-api-slice';
import { handleBackendError } from '../../utilities/errorHandling';
import SiteOpsTable from '../siteOpsTable';
import {
    UpcomingDemoTableColumns,
    UpcomingHERSRaterTableColumns,
    UpcomingModSetTableColumns,
    UpcomingStagingTableColumns,
    InteriorPunchWalkTableColumns,
    SelfPerform4WeekLookAheadTableColumns,
} from './siteOpsTablesColumns';

const getProjects = (projects, field) => {
    if (projects) {
        const today = new Date();
        return projects
            .filter((p) => p[field] && new Date(p[field]) >= today)
            .sort((a, b) => new Date(a[field]) - new Date(b[field]));
    }
    return [];
};

function SiteOpsDashboard() {
    const { data: siteOpsData, error, isLoading } = useGetSiteOpsQuery();

    useEffect(() => {
        if (error) {
            handleBackendError('Get Site Ops Data', error);
        }
    }, [error]);

    const upcomingDemoStarts = useMemo(
        () => getProjects(siteOpsData?.projects, 'Demo Start'),
        [siteOpsData?.projects]
    );

    const upcomingModSetStarts = useMemo(
        () => getProjects(siteOpsData?.projects, 'Mod Set Start'),
        [siteOpsData?.projects]
    );

    const upcomingStagingStarts = useMemo(
        () => getProjects(siteOpsData?.projects, 'Staging Start'),
        [siteOpsData?.projects]
    );

    const upcomingHERSRaterStarts = useMemo(
        () => getProjects(siteOpsData?.projects, 'HERS Rater'),
        [siteOpsData?.projects]
    );

    const interiorPunchWalkStarts = useMemo(
        () => getProjects(siteOpsData?.projects, 'Interior Punch Start'),
        [siteOpsData?.projects]
    );

    const selfPerform4WeekLookAheadProjects = useMemo(() => {
        if (siteOpsData?.look_ahead) {
            return Object.values(siteOpsData.look_ahead).sort(
                (a, b) => new Date(a['Start']) - new Date(b['Start'])
            );
        }
        return [];
    }, [siteOpsData?.look_ahead]);

    return (
        <Page>
            <div className="ps-0 ps-md-4 pe-2 pe-sm-0 pe-xs-0">
                <h1 className="mt-2 mb-3">Site Operations Dashboard</h1>
                {isLoading ? (
                    <Loader />
                ) : error ? null : (
                    <>
                        <div className="mb-3 d-flex flex-column flex-md-row gap-4">
                            <div>
                                <h2 className="fs-4 mb-0">Upcoming Demo Starts</h2>
                                <SiteOpsTable
                                    projects={upcomingDemoStarts}
                                    columnDefinitions={UpcomingDemoTableColumns}
                                />
                            </div>
                            <div>
                                <h2 className="fs-4 mb-0">Upcoming Mod Set Starts</h2>
                                <SiteOpsTable
                                    projects={upcomingModSetStarts}
                                    columnDefinitions={UpcomingModSetTableColumns}
                                />
                            </div>
                            <div>
                                <h2 className="fs-4 mb-0">Upcoming HERS Rater Starts</h2>
                                <SiteOpsTable
                                    projects={upcomingHERSRaterStarts}
                                    columnDefinitions={UpcomingHERSRaterTableColumns}
                                />
                            </div>
                        </div>
                        <div className="mb-3 d-flex flex-column flex-md-row gap-4">
                            <div>
                                <h2 className="fs-4 mb-0">Interior Punch Walk Starts</h2>
                                <SiteOpsTable
                                    projects={interiorPunchWalkStarts}
                                    columnDefinitions={InteriorPunchWalkTableColumns}
                                />
                            </div>
                            <div>
                                <h2 className="fs-4 mb-0">Upcoming Staging Starts</h2>
                                <SiteOpsTable
                                    projects={upcomingStagingStarts}
                                    columnDefinitions={UpcomingStagingTableColumns}
                                />
                            </div>
                        </div>
                        <div className="mb-4">
                            <h2 className="fs-4 mb-0">Self Perform 4 Week Look Ahead</h2>
                            <SiteOpsTable
                                projects={selfPerform4WeekLookAheadProjects}
                                columnDefinitions={SelfPerform4WeekLookAheadTableColumns}
                                containerHeight="450px"
                            />
                        </div>
                    </>
                )}
            </div>
        </Page>
    );
}

export default SiteOpsDashboard;
